import uniqBy from "lodash/uniqBy";

import type { Contact } from "app/pages/sales-intelligence/modules/ai-agent/types/meeting";

export const MAX_SUGGESTED_CONTACTS = 6;

export type SearchErrorVariant = "email" | "URL";

export const getSearchStringErrorVariant = (value: string | undefined): SearchErrorVariant | undefined => {
    if (value?.includes("@")) {
        return "email";
    } else if (value?.includes("linkedin.com/in/")) {
        return "URL";
    }

    return;
};

export const getContactKey = ({ name, title }: Pick<Contact, "name" | "title">): string => `${name}::${title ?? ""}`;

export const switchContactSelection = (contacts: Contact[], idToChange: string | undefined): Contact[] =>
    contacts.map((c) => (c.id === idToChange ? { ...c, isSelected: !c.isSelected } : c));

// Contacts with the same name and title look the same to the user (cards are told apart by name and title) and to the
// backend (only "name, title" is sent, see mapSelectedContactsToFormValue; email and source are not). Keeping one
// avoids identical cards and sending the same attendee twice, and keeps ids derived from the key unique.
const uniqContacts = (contacts: Contact[]): Contact[] => uniqBy(contacts, getContactKey);

export const mapContactsWithSelectionState = (contacts: Contact[], selectedContacts: Contact[]): Contact[] => {
    return uniqContacts(contacts).map((contact) => {
        const existingContact = selectedContacts.find((c) => c.name === contact.name && c.title === contact.title);
        return {
            ...contact,
            isSelected: existingContact?.isSelected || false,
            id: existingContact?.id || getContactKey(contact),
            isSuggested: true,
            isFromContactsList: true,
        };
    });
};

// Suggestions always keep their API position, ticked or not, so toggling a card never moves it.
// Every other contact (typed in, from the URL or a saved thread, picked from the search but not suggested)
// stays in front in `value` order.
export const mergeSelectedWithSuggestions = (selectedContacts: Contact[], suggestions: Contact[]): Contact[] => {
    const displayedSuggestions = uniqContacts(suggestions).slice(0, MAX_SUGGESTED_CONTACTS);
    const suggestionKeys = new Set(displayedSuggestions.map(getContactKey));
    const selectedKeys = new Set(selectedContacts.map(getContactKey));
    const isPlacedBySuggestions = (contact: Contact) =>
        Boolean(contact.isFromContactsList) && suggestionKeys.has(getContactKey(contact));

    const placedBySuggestions = new Map(
        selectedContacts.filter(isPlacedBySuggestions).map((contact) => [getContactKey(contact), contact]),
    );
    const leadingContacts = selectedContacts.filter((contact) => !isPlacedBySuggestions(contact));

    const suggestedContacts = displayedSuggestions.flatMap((suggestion): Contact[] => {
        const key = getContactKey(suggestion);
        const placed = placedBySuggestions.get(key);

        if (placed) {
            return [placed];
        }
        if (selectedKeys.has(key)) {
            return [];
        }

        return [{ ...suggestion, isSelected: false, id: key, isSuggested: true, isFromContactsList: true }];
    });

    return [...leadingContacts, ...suggestedContacts];
};

// Wraps around the list and skips disabled rows so arrow keys never land on an unselectable option.
export const getNextEnabledIndex = (disabledFlags: boolean[], currentIndex: number, direction: 1 | -1): number => {
    const total = disabledFlags.length;

    for (let step = 1; step <= total; step++) {
        const nextIndex = (((currentIndex + direction * step) % total) + total) % total;

        if (!disabledFlags[nextIndex]) {
            return nextIndex;
        }
    }

    return -1;
};
